import { useCallback, useState } from 'react';
import { resolveUploadLineAvailability } from '@/utils/product/upload-line-availability';
import { Product } from '../../../../types';

// EWM-949 — the quantity a row falls back to when the file does not carry a
// readable one. Matches the codes-only default in the Order Upload drawer
// (quick-order-upload), so the two upload surfaces agree.
const DEFAULT_QUANTITY = 1;

/**
 * EWM-949 — `parseInt(cell.trim() || '1')` had no radix and no guard, so a
 * non-numeric quantity ("abc") became NaN and flowed straight into
 * `Product.quantity` — and on into the cart add's `count`. A correctness bug
 * independent of any UI.
 *
 * Round 1 (R1-3) draws the line that matters: an UNREADABLE or ABSENT quantity
 * ("", "  ", "abc") is a MISREAD, and falls back to 1 — the same default the
 * Order Upload drawer applies to a codes-only file. An EXPLICIT `0` or a
 * negative number is NOT a misread: the customer typed a specific number, and
 * turning it into 1 would order a unit they did not ask for — a silent
 * invention of an order line, with the sign that costs money. A B2B reorder
 * sheet with zeroed lines is a real shape. Such a row is passed through at its
 * literal value, which `handleUploadClick` then leaves UNTICKED: it stays
 * visible, and the existing `checked[...]` filter in `handleAddToCart` keeps it
 * out of the cart — the same outcome as before this ticket, when the BFF
 * refused the non-positive count, but without the silence.
 *
 * `parseInt` still reads "2 units" as 2. That stays permissive deliberately:
 * this panel renders products, not rows, so it has nowhere to report a
 * rejection, and dropping the row would be the silent failure this ticket is
 * about. Strict quantity rejection lives in the Order Upload drawer, which has
 * a per-row reporting surface.
 */
const toQuantity = (raw: string | undefined): number => {
  const parsed = parseInt((raw ?? '').trim(), 10);
  return Number.isFinite(parsed) ? parsed : DEFAULT_QUANTITY;
};

const useProductHandlers = (
  csvProducts: Product[],
  readFiles: Record<string, string>,
  handleClearFiles: () => void,
  addToCart?: (
    lineItems: {
      sku: string;
      count: number;
    }[],
  ) => Promise<void>,
  handleSKUsUpdate?: (skus: string[]) => void,
  onClose?: () => void,
  // EWM-944 — BU `back-orders-allowed`. When true, an out-of-stock row is
  // ACCEPTED at its full requested quantity and flagged as back-ordering,
  // matching the file uploader and the PDP Add-to-Cart rules.
  backOrdersAllowed = false,
  // EWM-944 — BU `payment-terms`. "P" is a cash account, which cannot
  // back-order even when `back-orders-allowed` is true; the classifier applies
  // the full `isBackorderRestricted` rule the checkout gate enforces.
  paymentTerms?: string,
) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const memoizedClearFiles = useCallback(() => handleClearFiles(), [handleClearFiles]);
  const [loading, setLoading] = useState(false);

  const mappedProducts = products.map((product) => {
    const match = csvProducts.find((p) => p.sku === product.sku);
    // Pre-EWM-944 semantics: `inStock` meant "in stock AND enough of it".
    // Preserved so the in-stock badge still reflects real coverage.
    const stockCovers = !!match?.inStock && (match?.quantity ?? 0) >= product.quantity;

    const availability = resolveUploadLineAvailability({
      inStock: match?.inStock,
      availableQuantity: match?.quantity,
      requestedQuantity: product.quantity,
      removedFromSale: match?.removedFromSale,
      backOrdersAllowed,
      paymentTerms,
    });

    // Derived once and reused, so `willBackOrder` can never disagree with
    // `accepted`. Previously `willBackOrder` read the raw `availability`, so an
    // unknown SKU came back `{ accepted: false, willBackOrder: true }` —
    // harmless only because both render branches happen to test `exists` first.
    const accepted = !!match && availability.accepted;

    return {
      sku: product.sku,
      quantity: product.quantity,
      inStock: stockCovers,
      exists: !!match,
      accepted,
      // `availability.accepted` is tested first so TS narrows the union before
      // `backOrder` is read; `!!match` is the part that was missing before.
      willBackOrder: availability.accepted && !!match ? availability.backOrder : false,
      rejectionReason: availability.accepted ? undefined : availability.reason,
    };
  });

  const handleUploadClick = (files: File[]) => {
    const newProducts: Product[] = [];
    const newChecked = { ...checked };

    files.forEach((file) => {
      const formattedFile = readFiles[file.name]
        .split('\n')
        .slice(1)
        .filter(Boolean)
        // EWM-949 — KNOWN LIMITATION: a data line with no comma is DROPPED
        // here, silently. Reporting it needs a per-row rejection surface this
        // panel does not have (it renders products, not rows), so per-row
        // reporting on this surface is DEFERRED — tracked as EWM-1009. The
        // Order Upload drawer
        // (components/organisms/quick-order-upload) is the surface that reports
        // every line it cannot read. This filter is not correct as written — it
        // is scoped out, not signed off.
        .filter((l) => l.includes(','));

      const readProducts = formattedFile.map((l) => ({
        sku: l.split(',')[0].trim(),
        quantity: toQuantity(l.split(',')[1]),
      }));

      const skus = readProducts.map((p) => p.sku);

      handleSKUsUpdate?.(skus);

      readProducts.forEach((p) => {
        const product: Product = {
          sku: p.sku,
          quantity: p.quantity,
          inStock: false,
          exists: false,
        };

        const target = newProducts.find((productItem) => productItem.sku === product.sku);
        if (target) {
          target.quantity += product.quantity;
        } else {
          newProducts.push(product);
        }
      });
    });

    // EWM-949 round 1 (R1-3) — tick every row the customer can actually order,
    // and leave a row they explicitly zeroed (or made negative) UNTICKED. It
    // still renders, so nothing is silently dropped, and `handleAddToCart`'s
    // existing `checked[...]` filter keeps it out of the cart. Decided on the
    // MERGED quantity so a SKU repeated across rows is judged on the total.
    newProducts.forEach((product) => {
      newChecked[product.sku] = product.quantity > 0;
    });

    setChecked(newChecked);
    setProducts(newProducts);
  };

  const onCheckboxChange = (product: Product, value: boolean) => {
    const newChecked = { ...checked };
    newChecked[product.sku] = value;
    setChecked(newChecked);
  };

  const handleProductClear = () => setProducts([]);

  const handleAddToCart = () => {
    setLoading(true);
    // EWM-944 — add every ACCEPTED row, not just the fully-in-stock ones. For a
    // back-order-allowed customer that includes out-of-stock rows, at the full
    // requested quantity (AC 10 — never trimmed to available stock).
    const addableProducts = mappedProducts.filter((product) => checked[product.sku] && product.accepted);
    const lineItems = addableProducts.map((item) => {
      return { sku: item.sku, count: item.quantity };
    });
    addToCart?.(lineItems).then(() => {
      setLoading(false);
      onClose?.();
      handleProductClear();
      memoizedClearFiles();
    });
  };

  return {
    loading,
    checked,
    products: mappedProducts,
    handleUploadClick,
    handleProductClear,
    handleAddToCart,
    onCheckboxChange,
  };
};
export default useProductHandlers;
